import { useState } from 'react';

import { useModal } from '@/hooks/useModal';
import { useFundLPATerms } from '@/hooks/react-query/useFundLPATerms';
import { useActiveFeeBasisTypes } from '@/hooks/react-query/useLPATypes';
import { useUpdateFundRelationship } from '@/hooks/react-query/useInvestors';
import { percentageToDecimal } from '@/utils/formatters';
import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CustomLPATermsForm,
  type CustomLPATermsFormData,
} from '@/components/funds/CustomLPATermsForm';
import type { Fund } from '@/types/fund';
import type {
  InvestorFundRelationship,
  InvestorFundRelationshipUpdateData,
} from '@/types/investor';

interface EditCommitmentModalProps {
  open: boolean;
  fund: Fund;
  relationship: InvestorFundRelationship | null;
  onClose: () => void;
  onSaved?: () => void;
}

interface EditCommitmentFormData {
  commitment_amount: number | null;
  commitment_currency: string;
  commitment_date: string;
  status: 'ACTIVE' | 'PENDING' | 'EXITED' | 'DEFAULTED';
  has_custom_terms: boolean;
}

type EditCommitmentFormDataWithCustomTerms = EditCommitmentFormData &
  CustomLPATermsFormData;

function buildDefaultFormData(
  defaultCurrency: string
): EditCommitmentFormDataWithCustomTerms {
  return {
    commitment_amount: null,
    commitment_currency: defaultCurrency,
    commitment_date: '',
    status: 'ACTIVE',
    has_custom_terms: false,
  };
}

function buildFormDataFromRelationship(
  relationship: InvestorFundRelationship,
  defaultCurrency: string
): {
  formData: EditCommitmentFormDataWithCustomTerms;
  originalCustomTerms: Partial<CustomLPATermsFormData>;
} {
  const formData: EditCommitmentFormDataWithCustomTerms = {
    commitment_amount: relationship.commitment_amount ?? 0,
    commitment_currency: relationship.commitment_currency || defaultCurrency,
    commitment_date: relationship.commitment_date || '',
    status: relationship.status || 'ACTIVE',
    has_custom_terms: relationship.has_custom_terms || false,
  };

  const originalCustomTerms: Partial<CustomLPATermsFormData> = {};

  if (relationship.has_custom_terms) {
    const customInvestmentPeriodFeeRate =
      relationship.custom_investment_period_fee_rate
        ? Number.parseFloat(
            (relationship.custom_investment_period_fee_rate * 100).toFixed(10)
          ).toString()
        : undefined;
    const customPostInvestmentPeriodFeeRate =
      relationship.custom_post_investment_period_fee_rate
        ? Number.parseFloat(
            (relationship.custom_post_investment_period_fee_rate * 100).toFixed(
              10
            )
          ).toString()
        : undefined;
    const customHurdleRate = relationship.custom_hurdle_rate
      ? Number.parseFloat(
          (relationship.custom_hurdle_rate * 100).toFixed(10)
        ).toString()
      : undefined;
    const customCarriedInterest = relationship.custom_carried_interest
      ? Number.parseFloat(
          (relationship.custom_carried_interest * 100).toFixed(10)
        ).toString()
      : undefined;
    const customInvestmentPeriodFeeBasisId =
      relationship.custom_investment_period_fee_basis_id
        ? relationship.custom_investment_period_fee_basis_id.toString()
        : undefined;
    const customPostInvestmentPeriodFeeBasisId =
      relationship.custom_post_investment_period_fee_basis_id
        ? relationship.custom_post_investment_period_fee_basis_id.toString()
        : undefined;

    formData.custom_investment_period_fee_rate = customInvestmentPeriodFeeRate;
    formData.custom_post_investment_period_fee_rate =
      customPostInvestmentPeriodFeeRate;
    formData.custom_hurdle_rate = customHurdleRate;
    formData.custom_carried_interest = customCarriedInterest;
    formData.custom_investment_period_fee_basis_id =
      customInvestmentPeriodFeeBasisId;
    formData.custom_post_investment_period_fee_basis_id =
      customPostInvestmentPeriodFeeBasisId;

    originalCustomTerms.custom_investment_period_fee_rate =
      customInvestmentPeriodFeeRate;
    originalCustomTerms.custom_post_investment_period_fee_rate =
      customPostInvestmentPeriodFeeRate;
    originalCustomTerms.custom_hurdle_rate = customHurdleRate;
    originalCustomTerms.custom_carried_interest = customCarriedInterest;
    originalCustomTerms.custom_investment_period_fee_basis_id =
      customInvestmentPeriodFeeBasisId;
    originalCustomTerms.custom_post_investment_period_fee_basis_id =
      customPostInvestmentPeriodFeeBasisId;
  }

  return {
    formData,
    originalCustomTerms,
  };
}

export function EditCommitmentModal({
  open,
  fund,
  relationship,
  onClose,
  onSaved,
}: EditCommitmentModalProps) {
  const defaultCurrency = fund.base_currency;

  const initialModalState = relationship
    ? buildFormDataFromRelationship(relationship, defaultCurrency)
    : {
        formData: buildDefaultFormData(defaultCurrency),
        originalCustomTerms: {},
      };

  const [editFormData, setEditFormData] =
    useState<EditCommitmentFormDataWithCustomTerms>(initialModalState.formData);
  const originalCustomTerms =
    initialModalState.originalCustomTerms as Partial<CustomLPATermsFormData>;

  const fundLPATermsQuery = useFundLPATerms(fund.id);
  const feeBasisTypesQuery = useActiveFeeBasisTypes();
  const updateFundRelationshipMutation = useUpdateFundRelationship();
  const { modalProps } = useModal({
    isOpen: open,
    onClose,
  });

  const handleFormChange = (
    field: keyof EditCommitmentFormDataWithCustomTerms,
    value: string | boolean
  ) => {
    setEditFormData((previous) => {
      const updated = { ...previous, [field]: value };

      if (field === 'has_custom_terms' && value === true) {
        if (Object.keys(originalCustomTerms).length > 0) {
          updated.custom_investment_period_fee_rate =
            originalCustomTerms.custom_investment_period_fee_rate;
          updated.custom_post_investment_period_fee_rate =
            originalCustomTerms.custom_post_investment_period_fee_rate;
          updated.custom_hurdle_rate = originalCustomTerms.custom_hurdle_rate;
          updated.custom_carried_interest =
            originalCustomTerms.custom_carried_interest;
          updated.custom_investment_period_fee_basis_id =
            originalCustomTerms.custom_investment_period_fee_basis_id;
          updated.custom_post_investment_period_fee_basis_id =
            originalCustomTerms.custom_post_investment_period_fee_basis_id;
        } else if (!relationship && fundLPATermsQuery.data) {
          const lpaTerms = fundLPATermsQuery.data;

          updated.custom_investment_period_fee_rate = Number.parseFloat(
            (lpaTerms.investment_period_fee_rate * 100).toFixed(10)
          ).toString();
          updated.custom_post_investment_period_fee_rate = Number.parseFloat(
            (lpaTerms.post_investment_period_fee_rate * 100).toFixed(10)
          ).toString();
          updated.custom_hurdle_rate = Number.parseFloat(
            (lpaTerms.hurdle_rate * 100).toFixed(10)
          ).toString();
          updated.custom_carried_interest = Number.parseFloat(
            (lpaTerms.carried_interest * 100).toFixed(10)
          ).toString();
          updated.custom_investment_period_fee_basis_id =
            lpaTerms.investment_period_fee_basis_id.toString();
          updated.custom_post_investment_period_fee_basis_id =
            lpaTerms.post_investment_period_fee_basis_id.toString();
        }
      }

      return updated;
    });
  };

  const handleCustomTermsChange = (
    field: keyof CustomLPATermsFormData,
    value: string
  ) => {
    handleFormChange(field, value);
  };

  const handleSave = async () => {
    if (!relationship) {
      return;
    }

    const updateData: InvestorFundRelationshipUpdateData = {
      commitment_amount: editFormData.commitment_amount ?? 0,
      commitment_currency: editFormData.commitment_currency,
      commitment_date: editFormData.commitment_date || undefined,
      status: editFormData.status,
      has_custom_terms: editFormData.has_custom_terms,
      ...(editFormData.has_custom_terms && {
        custom_investment_period_fee_rate:
          editFormData.custom_investment_period_fee_rate
            ? percentageToDecimal(
                editFormData.custom_investment_period_fee_rate
              )
            : undefined,
        custom_post_investment_period_fee_rate:
          editFormData.custom_post_investment_period_fee_rate
            ? percentageToDecimal(
                editFormData.custom_post_investment_period_fee_rate
              )
            : undefined,
        custom_hurdle_rate: editFormData.custom_hurdle_rate
          ? percentageToDecimal(editFormData.custom_hurdle_rate)
          : undefined,
        custom_carried_interest: editFormData.custom_carried_interest
          ? percentageToDecimal(editFormData.custom_carried_interest)
          : undefined,
        custom_investment_period_fee_basis_id:
          editFormData.custom_investment_period_fee_basis_id
            ? Number(editFormData.custom_investment_period_fee_basis_id)
            : undefined,
        custom_post_investment_period_fee_basis_id:
          editFormData.custom_post_investment_period_fee_basis_id
            ? Number(editFormData.custom_post_investment_period_fee_basis_id)
            : undefined,
      }),
    };

    try {
      await updateFundRelationshipMutation.mutateAsync({
        relationshipId: relationship.id,
        data: updateData,
      });

      onClose();
      onSaved?.();
    } catch {
      // Error handling is handled by mutation toast middleware.
    }
  };

  const isSaving = updateFundRelationshipMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        {...modalProps}
      >
        <DialogHeader>
          <DialogTitle>Edit Commitment</DialogTitle>
          <DialogDescription className="sr-only">
            Update investor commitment details and optional custom LPA terms.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="commitment_amount">Commitment Amount *</Label>
            <CurrencyInput
              value={editFormData.commitment_amount}
              onChange={(value) =>
                setEditFormData((previous) => ({
                  ...previous,
                  commitment_amount: value,
                }))
              }
              currency={editFormData.commitment_currency}
              decimalPlaces={0}
              placeholder="Enter commitment amount"
            />
          </div>

          <div>
            <Label htmlFor="commitment_date">Commitment Date</Label>
            <Input
              type="date"
              value={editFormData.commitment_date}
              onChange={(event) =>
                setEditFormData((previous) => ({
                  ...previous,
                  commitment_date: event.target.value,
                }))
              }
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="has_custom_terms"
              checked={editFormData.has_custom_terms}
              onChange={(event) =>
                handleFormChange('has_custom_terms', event.target.checked)
              }
              className="rounded border-border"
            />
            <Label htmlFor="has_custom_terms">Custom terms</Label>
          </div>

          {editFormData.has_custom_terms && (
            <CustomLPATermsForm
              formData={{
                custom_investment_period_fee_rate:
                  editFormData.custom_investment_period_fee_rate,
                custom_investment_period_fee_basis_id:
                  editFormData.custom_investment_period_fee_basis_id,
                custom_post_investment_period_fee_rate:
                  editFormData.custom_post_investment_period_fee_rate,
                custom_post_investment_period_fee_basis_id:
                  editFormData.custom_post_investment_period_fee_basis_id,
                custom_hurdle_rate: editFormData.custom_hurdle_rate,
                custom_carried_interest: editFormData.custom_carried_interest,
              }}
              onChange={handleCustomTermsChange}
              standardTerms={fundLPATermsQuery.data}
              feeBasisTypes={feeBasisTypesQuery.data || []}
              isLoading={feeBasisTypesQuery.isLoading}
              className="border-2"
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || editFormData.commitment_amount == null}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
